import { useState } from "react";
import { EMPTY_FULL_RESPRAY } from "@/lib/pricing";

const emptySel = () => ({ selected: false, sprayMode: "full", touchupPrice: null, repair: "none", repairPrice: null, special: false, qty: 1 });
const EMPTY_DISCOUNT = { mode: "percent", value: "" };

// Holds everything the user picks for the current quote
export function useQuoteBuilder() {
  const [size, setSizeRaw] = useState("medium");
  const [selections, setSelections] = useState({});
  const [customPanels, setCustomPanels] = useState([]);
  const [services, setServices] = useState({});
  const [discount, setDiscount] = useState(EMPTY_DISCOUNT);

  const getSel = (key) => selections[key] || emptySel();
  const updateSel = (key, patch) => setSelections((s) => ({ ...s, [key]: { ...emptySel(), ...s[key], ...patch } }));
  // Changing the size drops any legacy saved-price overrides so panels re-price for the new size
  const setSize = (next) => {
    setSizeRaw(next);
    setSelections((s) => Object.fromEntries(Object.entries(s).map(([k, v]) => [k, { ...v, resprayPrice: null }])));
  };
  const toggleSelect = (key) => updateSel(key, { selected: !getSel(key).selected });
  const toggleService = (id, defaultPrice) => setServices((v) => {
    if (v[id] !== undefined) { const { [id]: _, ...rest } = v; return rest; }
    return { ...v, [id]: defaultPrice ? String(defaultPrice) : "" };
  });
  const setServicePrice = (id, price) => setServices((v) => ({ ...v, [id]: price }));

  const [customServices, setCustomServices] = useState([]);
  const addCustomService = ({ en, price }) => setCustomServices((l) => [...l, { id: `custom_svc_${Date.now()}`, en, price: Number(price) || 0 }]);
  const removeCustomService = (id) => setCustomServices((l) => l.filter((s) => s.id !== id));

  // Section 6 — full car respray; turning it on clears the per-panel picks (a full respray covers every panel)
  const [fullRespray, setFullResprayState] = useState(EMPTY_FULL_RESPRAY);
  const setFullRespray = (patch) => setFullResprayState((f) => {
    const next = { ...f, ...patch };
    if (f.scope === "off" && next.scope !== "off") { setSelections({}); setCustomPanels([]); next.size = next.size === "xl" ? "xl" : size; }
    if (patch.scope && patch.scope !== f.scope) next.priceOverride = null;
    if (patch.tier || patch.size) next.priceOverride = null;
    if (patch.paintType === "solid") next.paintSurcharge = ""; // surcharge box is hidden for solid/metallic
    return next;
  });

  const [parts, setParts] = useState([]);
  const addPart = ({ name, price, ri }) => setParts((l) => [...l, { id: `part_${Date.now()}`, name, price: Number(price) || 0, ri: Number(ri) || 0 }]);
  const updatePart = (id, patch) => setParts((l) => l.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePart = (id) => setParts((l) => l.filter((p) => p.id !== id));

  const addCustomPanel = ({ en, base_price }) => {
    const key = `custom_${Date.now()}`;
    setCustomPanels((c) => [...c, { key, en, zh: "", base_price: Number(base_price), custom: true }]);
    updateSel(key, { selected: true });
  };

  const removeCustomPanel = (key) => {
    setCustomPanels((c) => c.filter((p) => p.key !== key));
    setSelections((s) => {
      const n = { ...s };
      delete n[key];
      return n;
    });
  };

  const state = () => ({ selections, customPanels, services, customServices, parts, discount, fullRespray });
  const loadState = (st) => {
    if (!st) return;
    if (st.size) setSizeRaw(st.size);
    setSelections(st.selections || {});
    setCustomPanels(st.customPanels || []);
    setServices(st.services || {});
    setCustomServices(st.customServices || []);
    setParts(st.parts || []);
    setDiscount(st.discount || EMPTY_DISCOUNT);
    setFullResprayState({ ...EMPTY_FULL_RESPRAY, ...(st.fullRespray || {}) });
  };

  const clear = () => {
    setSelections({});
    setServices({});
    setCustomServices([]);
    setParts([]);
    setCustomPanels([]);
    setDiscount(EMPTY_DISCOUNT);
    setFullResprayState(EMPTY_FULL_RESPRAY);
  };

  const applyPreset = (preset) => {
    clear();
    setSize(preset.size);
    const sel = {};
    preset.panels.forEach((p) => { sel[p.key] = { selected: true, repair: p.repair || "none", repairPrice: p.repairPrice ?? null, special: !!p.special, qty: p.qty || 1 }; });
    setSelections(sel);
    if (preset.services) setServices(preset.services);
  };

  return {
    size, setSize, selections, customPanels, services, customServices, parts, discount, setDiscount, fullRespray, setFullRespray,
    getSel, updateSel, toggleSelect, toggleService, setServicePrice, addCustomService, removeCustomService,
    addPart, updatePart, removePart, addCustomPanel, removeCustomPanel, clear, applyPreset, state, loadState,
  };
}
